/**
 * Delete-vs-archive gating for a campaign project, shared by the campaign
 * workspace page (button choice) and its tests.
 *
 * A campaign-projects project's own `status` stays "draft" forever unless
 * explicitly archived — activation happens per-step, not on the project row
 * (see the backend `Campaign.status` docstring) — so `status === "draft"`
 * alone no longer means "nothing has happened yet" the way it did before the
 * project/send split. A legacy-consolidated or -imported project with real,
 * already-sent steps sits at `status === "draft"` with `rollup_status`
 * "active"/"closed"; `rollup_status` is what actually distinguishes a
 * genuine unsent draft (safe to hard-delete) from one with real send history
 * that only "Archive" may touch — matches the backend `delete_campaign`
 * action's own precondition.
 */

/** The fields the delete/archive decision depends on. */
export interface CampaignLifecycleInput {
  status: string;
  rollup_status: string;
}

/** True only for a project with no real send history at all — the one case
 * a hard delete is safe. */
export function canDeleteCampaign(campaign: CampaignLifecycleInput): boolean {
  return campaign.status === "draft" && campaign.rollup_status === "draft";
}

/** True for anything with real state to preserve that isn't already
 * terminal (archived/legacy) — everything `canDeleteCampaign` doesn't cover. */
export function canArchiveCampaign(campaign: CampaignLifecycleInput): boolean {
  return (
    campaign.status !== "archived" &&
    campaign.status !== "legacy" &&
    !canDeleteCampaign(campaign)
  );
}

/** True when no email send is still a draft — the one state in which the
 * backend's `update_campaign` lets a project's audience change. Tranche
 * membership is frozen from the audience at creation, so re-pointing a
 * project with a draft send would mislabel who that send goes to; once every
 * send has gone out the audience is only a label (e.g. a legacy import whose
 * real audience never existed in CIS). A project with no sends qualifies. */
export function canChangeCampaignAudience(campaign: {
  steps: { channel: string; status: string }[];
}): boolean {
  return !campaign.steps.some(
    (step) => step.channel === "email" && step.status === "draft",
  );
}
